import { Box, FormRadio, Grid, Typography } from "@husqvarna/ui-core";

import { useIntl } from "react-intl";
import { AmountUnit, LeasingPeriod, FinancingOption } from "../../models/Enums";
import RadioCardGroup from "../../../shared/components/RadioCardGroup/RadioCardGroup";
import ProposalPrice from "../../../shared/components/ProposalPrice/ProposalPrice";
import { Proposal } from "../../models/Proposal";
import FormTextWithSuffix from "../../../shared/components/FormTextWithSuffix";
import {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { PricingFinanceModel } from "../../models/PricingFinanceModel";

type ProposalFinancingFormProps = {
  proposal: Proposal;
  currency: string;
  register: UseFormRegister<PricingFinanceModel>;
  setValue: UseFormSetValue<PricingFinanceModel>;
  watch: UseFormWatch<PricingFinanceModel>;
  errors: any;
};

const ProposalFinancingForm = ({
  proposal,
  currency,
  register,
  setValue,
  watch,
  errors,
}: ProposalFinancingFormProps) => {
  const intl = useIntl();

  const financingOption = watch("financingOption");
  const discount = watch("financingInfo.discount");
  const discountUnit = watch("financingInfo.discountUnit");
  const leasingCost = watch("leasingInfo.leasingCost");

  return (
    <>
      <Box>
        <Typography
          variant="Body2Medium"
          mb={3}
          tag="h3"
          color="Grey600"
          text={intl.formatMessage({
            id: "project.proposal.financing.financeOption",
            defaultMessage: "Financial option",
          })}
        />
        <RadioCardGroup
          name="financingOption"
          value={financingOption}
          onChange={(val) =>
            setValue("financingOption", val, { shouldDirty: true })
          }
          options={[
            {
              label: intl.formatMessage({
                id: "financeOption.buy",
                defaultMessage: "Buy",
              }),
              description: intl.formatMessage({
                id: "financeOption.buy.description",
                defaultMessage: "One-time capital investment",
              }),
              value: FinancingOption.DirectPayment,
            },
            {
              label: intl.formatMessage({
                id: "financeOption.husqvarnaLeasePlus",
                defaultMessage: "Husqvarna Lease Plus",
              }),
              description: intl.formatMessage({
                id: "financeOption.husqvarnaLeasePlus.description",
                defaultMessage: "Recurring contract for 36 or 60 months",
              }),
              value: FinancingOption.Leasing,
            },
          ]}
        />
      </Box>

      <Box mt={8}>
        {financingOption === FinancingOption.DirectPayment && (
          <Box flexDirection="column" gap={2}>
            <Typography
              tag="label"
              color="Grey600"
              variant="Body2"
              htmlFor="offer"
              className="tcsp-medium"
              text={intl.formatMessage({
                id: "project.proposal.financing.offer.heading",
                defaultMessage: "Offer",
              })}
            />

            <FormTextWithSuffix
              id="offer"
              {...register("financingInfo.discount")}
              value={watch("financingInfo.discount")}
              error={errors.financingInfo?.discount?.message}
              suffix={discountUnit}
              placeholder={intl.formatMessage({id: 'general.amout', defaultMessage: 'Amount'})}
              suffixOptions={[
                { label: currency, value: AmountUnit.Currency },
                { label: "%", value: AmountUnit.Percent },
              ]}
              suffixRegister={register("financingInfo.discountUnit")}
              allowDecimals={false}
            />
          </Box>
        )}
        {financingOption === FinancingOption.Leasing && (
          <Box flexDirection='column'>
            <Typography
              mb={4}
              tag="label"
              color="Grey600"
              variant="Body2"
              className="tcsp-medium"
              text={intl.formatMessage({
                id: "project.proposal.financing.leasingPeriod.heading",
                defaultMessage: "Contract period",
              })}
            />
            <Box mb={10}>
              <Grid spacing={10} container alignItems="center">
                <Grid item>
                  <FormRadio
                    name="leasingInfo.leasingPeriod"
                    value={"36"}
                    checked={
                      watch("leasingInfo.leasingPeriod") ===
                      LeasingPeriod.Months36
                    }
                    onChange={() => {
                      setValue(
                        "leasingInfo.leasingPeriod",
                        LeasingPeriod.Months36,
                        { shouldDirty: true },
                      );
                    }}
                    label={intl.formatMessage({
                      id: "project.leasingPeriod.months36",
                      defaultMessage: "36 months",
                    })}
                  />
                </Grid>
                <Grid item>
                  <FormRadio
                    name="leasingInfo.leasingPeriod"
                    value={"60"}
                    checked={
                      watch("leasingInfo.leasingPeriod") ===
                      LeasingPeriod.Months60
                    }
                    onChange={() => {
                      setValue(
                        "leasingInfo.leasingPeriod",
                        LeasingPeriod.Months60,
                        { shouldDirty: true },
                      );
                    }}
                    label={intl.formatMessage({
                      id: "project.leasingPeriod.months60",
                      defaultMessage: "60 months",
                    })}
                  />
                </Grid>
              </Grid>
            </Box>
            <Typography
              mb={2}
              tag="label"
              color="Grey600"
              variant="Body2"
              className="tcsp-medium"
              htmlFor="leasingCost"
              text={intl.formatMessage({
                id: "project.proposal.financing.totalLeasingCost.header",
                defaultMessage: "Total leasing cost per month",
              })}
            />
            <FormTextWithSuffix
              id="leasingCost"
              {...register("leasingInfo.leasingCost")}
              value={watch("leasingInfo.leasingCost")}
              error={errors.leasingInfo?.leasingCost?.message}
              suffix={currency}
                      placeholder={intl.formatMessage({id: 'general.amout', defaultMessage: 'Amount'})}
              allowDecimals={false}
            />
          </Box>
        )}
      </Box>
      <Box
        flexDirection="row"
        justifyContent="space-between"
        alignItems="flex-end"
        mt={8}
      >
        <Typography variant="Microcopy" tag="p" pb={1} color={"Grey600"}>
          {intl.formatMessage({
            id: "project.proposal.discount.priceExlVatLabel",
            defaultMessage: "Price excl. VAT",
          })}
        </Typography>
        <ProposalPrice
          proposal={proposal}
          currency={currency}
          discountUnit={discountUnit}
          discount={discount}
          leasingAmount={leasingCost}
          financialOption={financingOption}
        />
      </Box>
    </>
  );
};

export default ProposalFinancingForm;
